import React, { useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css"; // Quill Editor styles
import { createMujjalah } from "./.request"; // API request function
import { useAuth } from "../context/AuthContext";
import pdf from '../../assets/icons/pdf.svg';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

const AddMujjalah = () => {
  const [year, setYear] = useState("");
  const [saal, setSaal] = useState("");
  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [content, setContent] = useState(""); // Content for Mujjalah
  const [pdfBase64, setPdfBase64] = useState(''); // PDF in Base64
  const [loading, setLoading] = useState(false);
  const { authToken } = useAuth();

  // Handle Image Upload
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        alert("Only image files are allowed.");
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        alert("File size should be less than 2MB.");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverImage(reader.result); // Store base64 for submission
        setCoverPreview(URL.createObjectURL(file)); // Use object URL for preview
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle PDF Upload
  const handlePdfUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== "application/pdf") {
        alert("Please upload a valid PDF file.");
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        alert("PDF size must be less than 10MB.");
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        setPdfBase64(reader.result);
      };
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!year || !saal || !coverImage || !content) {
      alert("Please fill in all fields before submitting (Year, Year in Urdu, Cover Image, Content, and PDF).");
      return;
    }

    const formData = {
      year,
      saal,
      coverImage,
      content,
      pdf: pdfBase64, // Include PDF in formData
    };

    setLoading(true);
    try {
      const response = await createMujjalah(authToken, formData);
      if (response?.Result) {
        alert("Mujjalah added successfully!");
        setYear("");
        setSaal("");
        setCoverImage(null);
        setCoverPreview(null);
        setContent("");
        setPdfBase64("");
      } else {
        alert(response?.data?.Msg || "Failed to add Mujjalah.");
      }
    } catch (error) {
      console.error("Error adding Mujjalah:", error);
      alert(error.response?.data?.message || "An error occurred while submitting the form.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto p-6 bg-white shadow-lg rounded-lg">
      <h2 className="text-2xl font-bold mb-4">Add Mujjalah</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Year Input */}
        <div>
          <label className="block font-semibold" htmlFor="year">
            Year:
          </label>
          <input
            id="year"
            type="text"
            className="w-full border p-2 rounded-md"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            placeholder="Enter year"
            aria-label="Mujjalah Year"
            required
          />
        </div>
        <div>
          <label className="block font-semibold" htmlFor="saal">
            Year (Urdu):
          </label>
          <input
            id="saal"
            type="text"
            className="w-full border p-2 rounded-md"
            value={saal}
            onChange={(e) => setSaal(e.target.value)}
            placeholder="Enter year in Urdu"
            aria-label="Year in Urdu"
            required
          />
        </div>

        {/* Cover Image Upload */}
        <div>
          <label className="block font-semibold" htmlFor="coverImage">
            Cover Image:
          </label>
          <input
            id="coverImage"
            type="file"
            className="w-full border p-2 rounded-md"
            accept="image/*"
            onChange={handleImageUpload}
            aria-label="Upload Cover Image"
            required
          />
          {coverPreview && (
            <div className="mt-3">
              <img
                src={coverPreview}
                alt="Cover Preview"
                className="w-40 h-32 object-cover rounded-md shadow"
              />
            </div>
          )}
        </div>

        {/* Content (Quill Editor) */}
        <div>
          <label className="block font-semibold" htmlFor="content">
            Content:
          </label>
          <ReactQuill
            id="content"
            value={content}
            onChange={setContent}
            className="bg-white"
            aria-label="Mujjalah Content Editor"
          />
        </div>

        {/* PDF Upload */}
        <div>
          <label className="block font-semibold" htmlFor="pdf">
            Upload PDF:
          </label>
          <input
            id="pdf"
            type="file"
            accept=".pdf"
            onChange={handlePdfUpload}
            className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            aria-label="Upload PDF file"
            required
          />
        </div>

        {/* PDF Download Link */}
        {pdfBase64 && (
          <div className="mt-4 flex items-center gap-2">
            <img src={pdf} alt="PDF icon" className="size-7" />
            <a
              href={pdfBase64}
              download={`${year || "mujjalah"}.pdf`}
              className="text-blue-500 underline"
            >
              Download PDF
            </a>
          </div>
        )}

        {/* Submit Button */}
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Submit"}
        </Button>
      </form>
    </div>
  );
};

export default AddMujjalah;